import { Action, Dispatch } from 'redux';
import { IAppState } from '../reducers';
import { selectUserOrgIds, selectUserOrgsSecurityProfiles } from '../../state/user/userOrgRole';
import { updateSecurityProfile } from '../../api/vault/securityProfiles/api';
import { updateSecurityProfile as updateLocalSecurityProfile } from '../../state/securityProfiles';
import {
  createVaultSubscription, getVaultSubscriptions, IVaultSubscription, updateVaultSubscription,
} from '../../api/vault/subscriptions';

export interface IState {
  isOpen: boolean;
  isLoading: boolean;
  isLoaded: boolean;
  movFormatEnabled: boolean;
  exchangeFileDownloadDisabled: boolean;
  subscription: IVaultSubscription;
}

export enum ActionType {
  Toggle = 'ContentPreferences.Toggle',
  StartLoading = 'ContentPreferences.StartLoad',
  FinishLoading = 'ContentPreferences.FinishLoad',
  UpdateMovFormatEnabled = 'ContentPreferences.UpdateMovFormatEnabled',
  UpdateExchangeFileDownloadDisabled = 'ContentPreferences.UpdateExchangeFileDownloadDisabled',
  UpdateSubscription = 'ContentPreferences.UpdateSubscription',
  Clear = 'ContentPreferences.Clear',
}
export interface IContentPreferencesAction extends Action<ActionType> {
  payload?: any,
}

const INITIAL_STATE: IState = {
  isOpen: false,
  isLoading: false,
  isLoaded: false,
  movFormatEnabled: false,
  exchangeFileDownloadDisabled: false,
  subscription: {
    orgId: -1,
    emailAddresses: [],
    phoneNumbers: [],
    subscribedTeams: [],
    isPushToCloud: false,
    isFirstAvailableOnly: false,
  },
};

export default function reducer(state: IState = INITIAL_STATE, action: IContentPreferencesAction): IState {
  switch (action.type) {
    case ActionType.Toggle:
      return {
        ...state,
        isOpen: !state.isOpen,
      };
    case ActionType.StartLoading:
      return {
        ...state,
        isLoading: true,
      };
    case ActionType.FinishLoading:
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
      };
    case ActionType.UpdateMovFormatEnabled:
      return {
        ...state,
        movFormatEnabled: action.payload.movFormatEnabled,
      };
    case ActionType.UpdateExchangeFileDownloadDisabled:
      return {
        ...state,
        exchangeFileDownloadDisabled: action.payload.exchangeFileDownloadDisabled,
      };
    case ActionType.UpdateSubscription:
      return {
        ...state,
        subscription: action.payload.subscription,
      };
    case ActionType.Clear:
      return INITIAL_STATE;
    default:
      return state;
  }
}

export const toggleContentPreferences = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.Toggle,
  });
};

export const fetchContentPreferences = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();

  dispatch({
    type: ActionType.StartLoading,
  });

  // Get mov enabled + exchange file download disabled
  const [{ mov_format_enabled, exchange_file_download_disabled }] = selectUserOrgsSecurityProfiles(state);

  // Get subscription settings
  const userOrgIds = selectUserOrgIds(state);
  const orgId = userOrgIds[0];

  if (userOrgIds.length > 1) {
    console.warn(`[QA] User belongs to multiple orgs -> targeting "${orgId}"`);
  }

  const response = await getVaultSubscriptions();
  let subscription: IVaultSubscription = (response.data?.data || []).find((sub) => sub.orgId === orgId);
  if (!subscription) {
    const createRes = await createVaultSubscription({
      orgId,
      phoneNumbers: [],
      emailAddresses: [],
      subscribedTeams: [],
      isPushToCloud: false,
      isFirstAvailableOnly: false,
    });
    if (createRes.status !== 201) {
      console.error(`[QA]: Error occurred while creating subscription settings for org "${orgId}"`);
      return;
    }
    subscription = createRes.data.data;
  }

  dispatch({
    type: ActionType.UpdateSubscription,
    payload: { subscription },
  });

  dispatch({
    type: ActionType.UpdateMovFormatEnabled,
    payload: { movFormatEnabled: mov_format_enabled },
  });

  dispatch({
    type: ActionType.UpdateExchangeFileDownloadDisabled,
    payload: { exchangeFileDownloadDisabled: exchange_file_download_disabled ?? false },
  });

  dispatch({
    type: ActionType.FinishLoading,
  });
};

export const clearContentPreferences = () => ({
  type: ActionType.Clear,
});

export const updateMovFormatEnabled = (movFormatEnabled: boolean) => ({
  type: ActionType.UpdateMovFormatEnabled,
  payload: {
    movFormatEnabled,
  },
});

export const updateExchangeFileDownloadDisabled = (exchangeFileDownloadDisabled: boolean) => ({
  type: ActionType.UpdateExchangeFileDownloadDisabled,
  payload: {
    exchangeFileDownloadDisabled,
  },
});

export const saveContentPreferences = () => async (
  dispatch: Dispatch<any>,
  getState: () => IAppState,
) => {
  const state = getState();
  const { exchange: { contentPreferences: { movFormatEnabled, exchangeFileDownloadDisabled } } } = state;

  // Save mov format enabled + exchange file download disabled
  const [securityProfile] = selectUserOrgsSecurityProfiles(state);
  const { _id } = securityProfile;
  const { status } = await updateSecurityProfile(_id, {
    mov_format_enabled: movFormatEnabled,
    exchange_file_download_disabled: exchangeFileDownloadDisabled,
  });

  if (status !== 200) {
    console.error(`[QA]: Could not save security profile "${_id}"`);
  } else {
    dispatch(updateLocalSecurityProfile({
      ...securityProfile,
      mov_format_enabled: movFormatEnabled,
      exchange_file_download_disabled: exchangeFileDownloadDisabled,
    }));
  }

  // Save subscription settings
  const { exchange: { contentPreferences: { subscription } } } = state;
  const response = await updateVaultSubscription(subscription);
  if (response.status !== 200) {
    // TODO: Note response status for success should be 204
    console.error(`[QA]: Could not save subscription "${subscription._id}"`);
  }
};

export const updateIsPushToCloud = (isPushToCloud: boolean) => async (
  dispatch: Dispatch<any>,
  getState: () => IAppState,
) => {
  const state = getState();
  dispatch({
    type: ActionType.UpdateSubscription,
    payload: {
      subscription: {
        ...state.exchange.contentPreferences.subscription,
        isPushToCloud,
      },
    },
  });
};

export const updateIsFirstAvailableOnly = (isFirstAvailableOnly: boolean) => async (
  dispatch: Dispatch<any>,
  getState: () => IAppState,
) => {
  const state = getState();
  dispatch({
    type: ActionType.UpdateSubscription,
    payload: {
      subscription: {
        ...state.exchange.contentPreferences.subscription,
        isFirstAvailableOnly,
      },
    },
  });
};
